import type {
  AssessedRiskRating,
  RiskAssessmentResponse,
  RiskAssessmentResult,
  RiskAssessmentResultStatus,
  RiskAssessmentScoredResult,
  RiskAssessmentSource
} from '@gcv/shared';
import { injectable } from 'inversify';

import { banksApi } from './api-util/api';

export interface GetRiskAssessmentsParams {
  dispensary_id?: string;
  status?: RiskAssessmentResultStatus;
  /** ISO date string; returns assessments due on or before this date. */
  due_before?: string;
}

export interface AssignRiskAssessmentPayload {
  /** Must reference a published template. */
  risk_assessment_template_id: string;
  /** ISO date string; defaults from the template's time to respond when omitted. */
  due_date?: string;
  source?: RiskAssessmentSource;
  /** Required when source is monitoring_review. */
  source_monitoring_review_id?: string;
}

export interface CompleteRiskAssessmentPayload {
  /** Defaults to the computed rating; supply to override. */
  final_rating?: AssessedRiskRating;
  /** Required when the rating is overridden. */
  justification?: string;
}

export interface OverrideRiskAssessmentRatingPayload {
  /** Omit (or null) to clear the override and fall back to the computed rating. */
  final_rating?: AssessedRiskRating | null;
  /** Required when setting an override. */
  justification?: string;
}

@injectable()
export class RiskAssessmentsApi {
  async getRiskAssessmentsForBank(
    bankId: string,
    params?: GetRiskAssessmentsParams
  ): Promise<RiskAssessmentResult[]> {
    return await banksApi().get(
      `/banks/${bankId}/risk-assessments`,
      params as Record<string, unknown> | undefined
    );
  }

  async getRiskAssessment(bankId: string, id: string): Promise<RiskAssessmentResult> {
    return await banksApi().get(`/banks/${bankId}/risk-assessments/${id}`);
  }

  // Autosave. The score, rating, and section breakdown are recomputed and persisted on every save.
  async saveRiskAssessmentResponses(
    bankId: string,
    id: string,
    responses: { [field_id: string]: RiskAssessmentResponse }
  ): Promise<RiskAssessmentScoredResult> {
    return await banksApi().put(`/banks/${bankId}/risk-assessments/${id}`, { responses });
  }

  async completeRiskAssessment(
    bankId: string,
    id: string,
    data: CompleteRiskAssessmentPayload
  ): Promise<RiskAssessmentScoredResult> {
    return await banksApi().post(`/banks/${bankId}/risk-assessments/${id}/complete`, data);
  }

  async overrideRiskAssessmentRating(
    bankId: string,
    id: string,
    data: OverrideRiskAssessmentRatingPayload
  ): Promise<RiskAssessmentResult> {
    return await banksApi().post(`/banks/${bankId}/risk-assessments/${id}/override`, data);
  }

  async assignRiskAssessment(
    bankId: string,
    dispensaryId: string,
    data: AssignRiskAssessmentPayload
  ): Promise<RiskAssessmentScoredResult> {
    return await banksApi().post(`/banks/${bankId}/dispensaries/${dispensaryId}/risk-assessments`, data);
  }
}
